import { useEffect, useRef, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { API } from "@/lib/api";
import { X, Sparkles, Send, Cpu } from "lucide-react";

const SUGGESTIONS = [
  "Summarize the top 3 critical alerts and recommend playbooks.",
  "Why is latency spiking in the Caruaru site?",
  "What's the right playbook for a brute-force SSH attempt?",
  "Draft an incident note for the SOC daily handoff.",
];

export default function Copilot({ open, onClose, initialPrompt }) {
  const { tenant } = useTenant();
  const [messages, setMessages] = useState([
    { role: "assistant", text: `Hi — I'm EVOS Copilot. Ask me anything about ${tenant?.short || "your tenant"}'s posture, alerts or playbooks.` },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  // Pre-fill the input when invoked with an initialPrompt
  useEffect(() => {
    if (open && initialPrompt) setInput(initialPrompt);
  }, [open, initialPrompt]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: q,
          context: `Tenant: ${tenant?.name} (${tenant?.short}). Sector: ${tenant?.sector}. Sites: ${tenant?.sites}. Users: ${tenant?.users}.`,
        }),
      });

      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + payload.delta };
                return copy;
              });
            }
            if (payload.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: `⚠ ${payload.error}` };
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "⚠ Copilot unavailable. Check connectivity." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="copilot-panel">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative w-full max-w-md h-full bg-[#172554] border-l border-[#1E3A8A] flex flex-col">
        <div className="h-16 border-b border-[#1E3A8A]/60 px-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1E3A8A] flex items-center justify-center rounded-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-base tracking-tight">EVOS Copilot</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#93C5FD] font-mono flex items-center gap-2">
                <Cpu className="w-2.5 h-2.5" /> Claude Sonnet 4.5
              </div>
            </div>
          </div>
          <button data-testid="close-copilot-btn" onClick={onClose} className="text-slate-400 hover:text-white p-2">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[88%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap rounded-sm ${
                  msg.role === "user"
                    ? "bg-[#1E3A8A] text-white border border-[#1D4ED8]"
                    : "bg-[#0F172A]/60 border border-[#1E3A8A]/40 text-slate-200"
                }`}
              >
                {msg.text || (streaming && i === messages.length - 1 ? <span className="evos-blink">▍</span> : null)}
              </div>
            </div>
          ))}
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-3 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#93C5FD]/70 font-mono mb-2">Suggested prompts</div>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="w-full text-left text-xs px-3 py-2 border border-[#1E3A8A]/40 hover:bg-[#1E3A8A]/30 text-slate-300 transition-colors rounded-sm"
                data-testid={`suggestion-${s.slice(0,12)}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#1E3A8A]/60 p-4 flex gap-2"
        >
          <input
            data-testid="copilot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={streaming ? "Copilot is thinking..." : "Ask EVOS Copilot..."}
            disabled={streaming}
            className="flex-1 bg-[#020617] border border-[#1E3A8A]/60 rounded-sm px-3 py-2 text-sm text-slate-100 focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6] outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            data-testid="copilot-send"
            disabled={streaming || !input.trim()}
            className="bg-[#1E3A8A] hover:bg-[#1D4ED8] disabled:opacity-50 text-white px-4 rounded-sm transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </aside>
    </div>
  );
}
